import { LocationOn as LocationOnIcon } from '@mui/icons-material';
import { Map } from 'shared/map-exports';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { LOCATION_ZOOM_LEVEL } from 'features/mapping/mapping.constants';
import { WildfireGeoEventData } from 'features/wildfires/wildfireGeoEventData.types';
import { UrgencyIcon } from 'features/geoEvents/components/UrgencyIcon';
import {
  Flooding,
  FloodingGeoEventData,
} from 'features/flooding/flooding.types';
import { getViewGeoEventLink } from 'features/geoEvents/geoEvent.utils';
import { ReactNode } from 'react';
import { History, LocationDescriptor } from 'history';
import { buildLocationState } from 'hooks/useLocationState';
import { hideKeyboardAndWait } from 'utils/hideKeyboardAndWait';
import { getSearchCardMinSnapPx } from '../../constants';
import { LatLng, MapLocation } from '../../shared/types';
import {
  getMapSearchResultFormattedAddress,
  hasValidCoordinates,
} from '../../shared/utils';
import { GeocodeResult, SearchSource } from './useMapSearch.types';
import {
  fetchGeoEventsForSearch,
  fetchGoogleMapsSearch,
  fetchMapboxSearch,
  getGeoEventAvatar,
  getGeoEventDetails,
  geoEventHasInternalNotes,
  getGoogleCoords,
  getLatLngFromString,
  getMapSearchResultName,
} from './useMapSearch.utils';

type GetMapsSearchSourceProps = {
  map?: Map;
  history: History;

  // @todo remove with 'pin_drop_improvements' release flag:
  setSelectedValue?: (value: GeocodeResult) => void;
  setSelectedLocation?: (location: MapLocation) => void;
};

const placeIconSx = {
  color: 'accent.main',
};

export const getUrgencyIcon = (
  data: WildfireGeoEventData | FloodingGeoEventData,
): ReactNode => {
  const urgency = 'urgency' in data ? data.urgency : null;
  return urgency ? <UrgencyIcon urgency={urgency} size={19} /> : null;
};

// Reads the route from `history` when routing. Search results are cached with
// their click handlers, so a route captured at render time can be stale.
const buildRoutingForLatLngPinDrop = (
  history: History,
  { lat, lng }: LatLng,
): LocationDescriptor => {
  const { pathname, search } = history.location;

  return {
    pathname: `/@${lat},${lng},${LOCATION_ZOOM_LEVEL}z/pin`,
    state: buildLocationState(`${pathname}${search}`),
    // send current ?search to pin drawer so results stay and don't
    // have to be re-fetched
    search,
  };
};

export const getGeoEventSearchSource = (
  isHistorical = false,
): SearchSource<Wildfire | Flooding> => ({
  sourceId: 'geo-event',
  action: 'link',
  fetchFn: async (searchQuery) => {
    const results = await fetchGeoEventsForSearch(searchQuery, isHistorical);
    return results;
  },
  getId: (geoEvent) => geoEvent.id,
  getPrimaryText: (geoEvent) => geoEvent.name,
  getSecondaryText: (geoEvent) => geoEvent.address ?? undefined,
  getGeoEventDetails,
  getAvatar: (geoEvent) => getGeoEventAvatar(geoEvent, isHistorical),
  getHref: (geoEvent) =>
    getViewGeoEventLink(geoEvent.geoEventType ?? 'wildfire', geoEvent.id),
  getUrgencyIcon: (geoEvent) => getUrgencyIcon(geoEvent.data),
  getHasInternalNotes: geoEventHasInternalNotes,
});

export const getInactiveGeoEventSearchSource = (): SearchSource<
  Wildfire | Flooding
> => ({
  ...getGeoEventSearchSource(true),
  sourceId: 'inactive-geo-event',
  getGeoEventDetails,
});

const easeMapToPin = (map: Map | undefined, lng: number, lat: number): void => {
  if (!map) return;
  if (!hasValidCoordinates({ lat, lng })) return;

  setTimeout(() => {
    map.easeTo({
      center: [lng, lat],
      zoom: LOCATION_ZOOM_LEVEL,
      offset: [0, -getSearchCardMinSnapPx()],
    });
  }, 0);
};

// Module-level because sources are rebuilt on every render. Ignores further
// taps (e.g. a double-tap) while a search result pin drop is waiting to route.
let isSearchResultClickPending = false;

/**
 * Unfortunately, we can't just make search results links with
 * `{ action: 'link', getHref: () => string }` because Google and Mapbox don't include
 * lat/lng with results. We need to ask for lat/lng separately, which means
 * we would need to pre-fetch lat/lng for every search result at display
 * time instead of click time.
 *
 * The keyboard must be fully hidden before routing: the pin drawer's height
 * and the map's centering offset are measured from `window.innerHeight` on
 * mount, which is keyboard-shrunk while the keyboard is up.
 */
const handleResultClick = async (args: {
  history: History;
  getLatLng: () => LatLng | Promise<LatLng | null>;
}): Promise<void> => {
  const { history, getLatLng } = args;
  if (isSearchResultClickPending) return;

  isSearchResultClickPending = true;
  try {
    const [latLng] = await Promise.all([getLatLng(), hideKeyboardAndWait()]);
    if (latLng) {
      history.push(buildRoutingForLatLngPinDrop(history, latLng));
    }
  } finally {
    isSearchResultClickPending = false;
  }
};

const fetchGoogleLatLng = async (placeId: string): Promise<LatLng | null> => {
  const response = await getGoogleCoords(placeId);
  if (!response) return null;

  const [lng, lat] = response.coords;
  return hasValidCoordinates({ lat, lng }) ? { lat, lng } : null;
};

export const getGoogleMapsSearchSource = (
  props: GetMapsSearchSourceProps,
): SearchSource<GeocodeResult> => {
  const { map, history, setSelectedValue, setSelectedLocation } = props;

  const pinImprovements = !setSelectedValue || !setSelectedLocation;

  return {
    sourceId: 'google',
    action: 'click',
    fetchFn: fetchGoogleMapsSearch,
    getId: (result) => result.id,
    getPrimaryText: (result) => getMapSearchResultName(result),
    getSecondaryText: (result) =>
      getMapSearchResultFormattedAddress(
        getMapSearchResultName(result),
        result.placeName,
      ),
    getAvatar: () => <LocationOnIcon fontSize="large" sx={placeIconSx} />,
    onClick: async (result) => {
      // Map search result is a google place prediction, get place by id
      if (pinImprovements) {
        await handleResultClick({
          history,
          getLatLng: () => fetchGoogleLatLng(result.id),
        });
        return;
      }

      const response = await getGoogleCoords(result.id);
      if (response) {
        const [lng, lat] = response.coords;
        if (!hasValidCoordinates({ lat, lng })) return;

        const mapLocation: GeocodeResult = {
          ...result,
          center: response.coords,
          placeAddress: response.formattedAddress,
        };

        // setSelectedValue enables the 'save' button, setSelectedLocation drops the pin
        setSelectedValue(mapLocation);
        setSelectedLocation({
          name: getMapSearchResultName(result),
          lat,
          lng,
          place: false,
          type: 'LatLngLocation',
        });

        easeMapToPin(map, lng, lat);
      }
    },
  };
};

export const getMapboxMapsSearchSource = (
  props: GetMapsSearchSourceProps,
): SearchSource<GeocodeResult> => {
  const { map, history, setSelectedValue, setSelectedLocation } = props;

  const pinImprovements = !setSelectedValue || !setSelectedLocation;

  return {
    sourceId: 'mapbox',
    action: 'click',
    fetchFn: fetchMapboxSearch,
    getId: (result) => result.id,
    getPrimaryText: (result) => getMapSearchResultName(result),
    getSecondaryText: (result) =>
      getMapSearchResultFormattedAddress(
        getMapSearchResultName(result),
        result.placeName,
      ),
    getAvatar: () => <LocationOnIcon fontSize="large" sx={placeIconSx} />,
    onClick: async (result) => {
      const [lng, lat] = result.center;
      if (!hasValidCoordinates({ lat, lng })) return;

      if (pinImprovements) {
        await handleResultClick({ history, getLatLng: () => ({ lat, lng }) });
        return;
      }

      // setSelectedValue enables the 'save' button, setSelectedLocation drops the pin
      setSelectedValue(result);
      setSelectedLocation({
        name: getMapSearchResultName(result),
        lat,
        lng,
        place: false,
        type: 'LatLngLocation',
      });

      easeMapToPin(map, lng, lat);
    },
  };
};

export const getLatLngSearchSource = (
  props: GetMapsSearchSourceProps,
): SearchSource<LatLng> => {
  const { map, history, setSelectedValue, setSelectedLocation } = props;

  const pinImprovements = !setSelectedValue || !setSelectedLocation;

  return {
    sourceId: 'latlng',
    action: 'click',
    fetchFn: async (searchQuery) => {
      const latLng = getLatLngFromString(searchQuery);
      return Promise.resolve(latLng ? [latLng] : []);
    },
    getId: (latLng) => latLng.toString(),
    getPrimaryText: (latLng) => `${latLng.lat}, ${latLng.lng}`,
    getSecondaryText: () => '',
    getAvatar: () => <LocationOnIcon fontSize="large" sx={placeIconSx} />,
    onClick: async (latLng) => {
      const { lat, lng } = latLng;

      if (pinImprovements) {
        // Technically we should use a link with `{ action: 'link', getHref: () => string }`,
        // but since Google/Mapbox above don't support that, it's simpler to keep
        // everything working the same way.
        await handleResultClick({ history, getLatLng: () => ({ lat, lng }) });
        return;
      }

      // setSelectedValue enables the 'save' button, setSelectedLocation drops the pin
      setSelectedValue({
        id: latLng.toString(),
        type: 'LatLngLocation',
        text: `${latLng.lat}, ${latLng.lng}`,
        placeName: `${latLng.lat}, ${latLng.lng}`,
        center: [lng, lat],
      });
      setSelectedLocation({
        name: `${latLng.lat}, ${latLng.lng}`,
        lat,
        lng,
        place: false,
        type: 'LatLngLocation',
      });

      easeMapToPin(map, lng, lat);
    },
  };
};
